import type { InputHTMLAttributes } from 'react'

/**
 * DOM contract every ZIP <input> must carry. Spread into the element.
 * - name / autoComplete "postal-code" are LOAD-BEARING: they enable browser + iOS
 *   postal-code autofill AND trigger PostHog's maskInputFn value-masking
 *   (app/providers.tsx) so the ZIP is asterisked in session replay.
 * - data-1p-ignore / data-lpignore / data-form-type suppress password-manager icons
 *   on this non-credential field (parity with the ZipCodeInput island).
 *
 * NOTE: id, aria-label, placeholder, pattern, value, onChange, and styling stay
 * per-input and are NOT part of this contract.
 *
 * @types/react 19.x InputHTMLAttributes has no catch-all data-* index signature,
 * so we type the contract explicitly rather than `satisfies InputHTMLAttributes`.
 */
type ZipInputAttrs = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'name' | 'type' | 'inputMode' | 'autoComplete' | 'maxLength'
> & {
  'data-1p-ignore': string
  'data-lpignore': string
  'data-form-type': string
}

export const ZIP_INPUT_ATTRS: ZipInputAttrs = {
  name: 'postal-code',
  type: 'text',
  inputMode: 'numeric',
  autoComplete: 'postal-code',
  maxLength: 5,
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-form-type': 'other',
}

/** Strip non-digits, cap at 5. Single source for the ZIP input mask. */
export function sanitizeZip(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 5)
}
